// Iter 134h — Calendar / date helpers.
//
// PROBLEM:
//   `new Date("2026-05-15")` is parsed as UTC midnight, then rendered
//   in the BROWSER'S LOCAL TIMEZONE.  For any user west of UTC (Americas),
//   that displays as "May 14" instead of "May 15" — off-by-one bug.
//
// SOLUTION:
//   Treat YYYY-MM-DD strings as a *calendar date* (timezone-free) and
//   render them with explicit local-day construction so the rendered
//   month/day always matches the date string the backend issued.

/**
 * Parse a YYYY-MM-DD calendar date string into a JS Date object anchored
 * to LOCAL midnight (not UTC).  This guarantees `getDate()`,
 * `getMonth()`, and `getFullYear()` return the values that match the
 * input string regardless of the user's timezone.
 *
 * Accepts:
 *   - "2026-05-15"           → local midnight on 2026-05-15
 *   - "2026-05-15T20:00:00Z" → returned untouched (full ISO already has tz)
 *
 * Returns null for any unparseable input.
 */
export const parseCalendarDate = (input) => {
  if (!input) return null;
  // Full ISO timestamp — let JS handle it (timezone-anchored).
  if (typeof input === "string" && input.length > 10 && input.includes("T")) {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = typeof input === "string" ? input : String(input);
  // Strict YYYY-MM-DD shape.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const [, y, mo, da] = m;
  // Construct via Y/M/D constructor → uses local timezone, so getDate()
  // returns the same day as the input string.
  return new Date(Number(y), Number(mo) - 1, Number(da));
};

/**
 * Format a YYYY-MM-DD or ISO timestamp as a short month/day pill
 * (e.g. "May 15") in the user's locale.
 */
export const formatShortDate = (input, locale = "en-US") => {
  const d = parseCalendarDate(input);
  if (!d) return "—";
  return d.toLocaleDateString(locale, { month: "short", day: "numeric" });
};

/**
 * Format a YYYY-MM-DD or ISO timestamp as a weekday + month/day
 * (e.g. "Fri, May 15").
 */
export const formatWeekdayDate = (input, locale = "en-US") => {
  const d = parseCalendarDate(input);
  if (!d) return "—";
  return d.toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });
};

/**
 * Local-calendar day-of-year key, e.g. "2026-05-15", from a JS Date
 * (anchored to whatever timezone the Date is in).
 */
export const toCalendarKey = (d) => {
  if (!d || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

/**
 * Return "Today" / "Yesterday" / "Tomorrow" / weekday-short for the
 * given YYYY-MM-DD relative to the user's LOCAL calendar today.  This
 * ensures the label matches what the user perceives as "today" in their
 * timezone, rather than being anchored to UTC.
 */
export const relativeDayLabel = (input, locale = "en-US") => {
  const d = parseCalendarDate(input);
  if (!d) return "—";
  const todayKey = toCalendarKey(new Date());
  const inputKey = toCalendarKey(d);
  if (todayKey === inputKey) return "Today";
  // Build yesterday / tomorrow keys from local today.
  const t = new Date();
  const yesterday = new Date(t.getFullYear(), t.getMonth(), t.getDate() - 1);
  const tomorrow = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1);
  if (toCalendarKey(yesterday) === inputKey) return "Yesterday";
  if (toCalendarKey(tomorrow) === inputKey) return "Tomorrow";
  return d.toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });
};

/**
 * "Today" if the kickoff timestamp (unix seconds OR ISO string) falls
 * on the user's local calendar today.
 */
export const isToday = (input) => {
  const d = parseCalendarDate(input);
  if (!d) return false;
  return toCalendarKey(d) === toCalendarKey(new Date());
};
